import React from 'react';
import { Player } from '../../types';
import { useResponsive } from '../../hooks/useResponsive';

interface EmptySlotProps {
  index: number;
  isOccupying: boolean;
  userIsCreator: boolean;
  onJoinPosition: () => void;
  matchStatus?: string;
  reportCompleted?: boolean;
  // Nuevas props para drag & drop
  onDragOver?: (player: Player | null) => void;
  onDrop?: (player: Player | null, teamIndex?: number, positionIndex?: number) => void;
  isDragging?: boolean;
  draggedPlayer?: Player | null;
  dragOverPlayer?: Player | null;
  teamIndex?: number;
}

export const EmptySlot: React.FC<EmptySlotProps> = ({
  index,
  isOccupying,
  userIsCreator,
  onJoinPosition,
  matchStatus,
  reportCompleted,
  onDragOver,
  onDrop,
  isDragging = false,
  draggedPlayer,
  dragOverPlayer,
  teamIndex
}) => {
  const { isMobile, isCompact } = useResponsive();
  const isDesktop = !isMobile && !isCompact;
  const handleDragOver = (e: React.DragEvent) => {
    if (!userIsCreator || !draggedPlayer || isOccupying) {
      e.preventDefault();
      return;
    }
    
    // Permitir drag over para suplentes (promover a titular) y titulares (mover a posición específica)
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    onDragOver?.(null); // null indica que es un cupo vacío
  };

  const handleDrop = (e: React.DragEvent) => {
    if (!userIsCreator || !draggedPlayer || isOccupying) {
      e.preventDefault();
      return;
    }
    
    // Permitir drop para suplentes (promover a titular) y titulares (mover a posición específica)
    e.preventDefault();
    onDrop?.(null, teamIndex, index); // null indica que es un cupo vacío, más teamIndex y positionIndex
  };

  const isDragOver = dragOverPlayer === null && isDragging && !isOccupying;

  return (
    <div
      style={{
        padding: isDesktop ? '5px 10px' : '12px',
        margin: isDesktop ? '0' : '8px 0',
        backgroundColor: isDragOver ? 'rgba(255, 215, 0, 0.2)' : (isOccupying ? '#fff3cd' : '#23272A'),
        borderRadius: isDesktop ? '8px' : '12px',
        border: isDragOver ? '2px dashed #FFD700' : (isOccupying ? '2px dashed #ff6b35' : '2px dashed #33383B'),
        cursor: (!isOccupying && userIsCreator) ? 'pointer' : 'default',
        display: 'flex',
        alignItems: 'center',
        gap: isDesktop ? '8px' : '8px',
        transition: 'all 0.2s ease',
        position: 'relative',
        transform: isDragOver ? 'scale(1.05)' : 'scale(1)',
        flex: isDesktop ? 1 : undefined,
        minHeight: 0,
      }}
      onClick={(!isOccupying && userIsCreator) ? onJoinPosition : undefined}
      onKeyDown={(!isOccupying && userIsCreator) ? (e) => { if (e.key === 'Enter' || e.key === ' ') onJoinPosition?.(); } : undefined}
      role={(!isOccupying && userIsCreator) ? 'button' : undefined}
      tabIndex={(!isOccupying && userIsCreator) ? 0 : undefined}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {isDragOver && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(255, 215, 0, 0.3)',
          border: '2px dashed #FFD700',
          borderRadius: '12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10,
          fontSize: '14px',
          fontWeight: 'bold',
          color: '#FFD700',
          textShadow: '0 0 4px rgba(0,0,0,0.8)'
        }}>
          {draggedPlayer?.isSubstitute ? '➕ PROMOVER A TITULAR' : '📋 MOVER A POSICIÓN'}
        </div>
      )}
      <div style={{
        backgroundColor: isOccupying ? '#ff6b35' : '#6C757D',
        color: 'white',
        borderRadius: '50%',
        width: isDesktop ? '26px' : '32px',
        height: isDesktop ? '26px' : '32px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: isDesktop ? '11px' : '14px',
        fontWeight: 'bold',
        flexShrink: 0,
      }}>
        {isOccupying ? '⏳' : (index + 1)}
      </div>
      <div style={{ flex: 1 }}>
        {isOccupying ? (
          isDesktop ? (
            <div style={{ fontWeight: '700', color: '#ff6b35', fontSize: '13px' }}>
              Anotandose...
            </div>
          ) : (
            <>
              <div style={{ fontWeight: '700', color: '#ff6b35', fontSize: '16px' }}>
                Jugador Anotandose...
              </div>
              <div style={{ fontSize: '12px', color: '#ff6b35', fontStyle: 'italic' }}>
                Completando registro...
              </div>
            </>
          )
        ) : reportCompleted ? (
          isDesktop ? (
            <div style={{ fontWeight: '700', color: '#666', fontSize: '13px' }}>
              Reporte Finalizado
            </div>
          ) : (
            <>
              <div style={{ fontWeight: '700', color: '#666', fontSize: '16px' }}>
                Reporte Finalizado
              </div>
              <div style={{ fontSize: '12px', color: '#999', fontStyle: 'italic' }}>
                No se pueden agregar jugadores
              </div>
            </>
          )
        ) : (
          isDesktop ? (
            <div style={{ fontWeight: '700', color: '#B0BEC5', fontSize: '13px' }}>
              Cupo disponible
            </div>
          ) : (
            <>
              <div style={{ fontWeight: '700', color: '#B0BEC5', fontSize: '16px' }}>
                Cupo disponible
              </div>
              <div style={{ fontSize: '12px', color: '#757575', fontStyle: 'italic' }}>
                Esperando jugador...
              </div>
            </>
          )
        )}
      </div>
    </div>
  );
}; 